import React from "react";
import ResponsiveECharts from "./ResponsiveECharts.jsx";
import { ALEA_INFO } from "../constants.js";
import { TAILLE_TEXTE, COULEUR_TEXTE } from "./echartsTheme";

// Même logique que dans utils/gantt.js : texte blanc sur fond foncé, noir
// sur fond clair.
function luminance(hex) {
  const clean = hex.replace("#", "");
  const v = clean.match(/\w\w/g).map((h) => parseInt(h, 16));
  return (0.299 * v[0] + 0.587 * v[1] + 0.114 * v[2]) / 255;
}

// Ordre d'affichage des groupes : les années se trient numériquement, mais
// en TRACC les groupes sont des chaînes ("reference", "+2", "+2.7") qu'une
// simple soustraction ne sait pas ordonner.
function rangGroupe(groupe) {
  if (groupe === "reference") return -Infinity;
  const n = Number(groupe);
  return Number.isNaN(n) ? Infinity : n;
}

/**
 * @param afficherCourbe  true : courbe "Total" + camemberts (page Comprendre).
 *                        false : camemberts seuls (Impacts globaux).
 * @param libelleAnnee    (optionnel) libellé affiché sous chaque camembert à
 *                        partir de son YearGroup ; par défaut le YearGroup brut.
 */
export default function PertesChart({ data, risque, rcp, afficherCourbe = true, libelleAnnee = String }) {
  const { total, repartition } = data;
  const totalSorted = [...total].sort((a, b) => rangGroupe(a.YearGroup) - rangGroupe(b.YearGroup));
  const years = totalSorted.map((d) => d.YearGroup);

  const aleaCodes = Object.keys(ALEA_INFO);

  // Rayon proportionnel à la valeur totale de l'année (racine carrée, pas
  // linéaire, pour que ce soit la SURFACE du camembert qui soit
  // proportionnelle, pas juste son rayon) — le plus grand total obtient 95%,
  // les autres sont mis à l'échelle en conséquence.
  const maxTotal = Math.max(...totalSorted.map((d) => d.val), 0);
  const rayonPour = (val) => {
    if (maxTotal <= 0) return "70%";
    const ratio = Math.max(0, val) / maxTotal;
    return `${Math.round(95 * Math.sqrt(ratio))}%`;
  };

  const lineOption = {
    tooltip: { trigger: "axis" },
    grid: { left: 60, right: 20, top: 50, bottom: 40, containLabel: true },
    xAxis: { type: "category", data: years, boundaryGap: false, axisLabel: { fontSize: 14, formatter: (v) => String(v) } },
    yAxis: { type: "value", name: `Perte ${risque} (%)`, nameLocation: "end", nameGap: 15, nameTextStyle: { fontSize: 13, fontWeight: 600, color: "#000", align: "left" } },
    series: [
      {
        name: "Total",
        type: "line",
        symbol: "circle",
        symbolSize: 10,
        lineStyle: { width: 2.5, color: rcp === "tracc" ? "#c927be" : "#c9a227" },
        itemStyle: { color: rcp === "tracc" ? "#c927be" : "#c9a227" },
        data: totalSorted.map((d) => d.val),
        animation: false,
      },
    ],
  };

  const pieOptionFor = (year) => {
    const rows = repartition.filter((r) => r.YearGroup === year);
    const totalVal = totalSorted.find((d) => d.YearGroup === year)?.val ?? 0;
    return {
      // Même tooltip que les camemberts des cultures (RepartitionCulturesChart).
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      tooltip: {
        trigger: "item",
        textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
        valueFormatter: (valeur) => `${valeur} %`,
      },
      series: [
        {
          type: "pie",
          // Sur Impacts globaux les camemberts sont de taille fixe, identique
          // à celle des camemberts des cultures juste en dessous.
          radius: afficherCourbe ? rayonPour(totalVal) : "92%",
          center: ["50%", "50%"],
          label: {
            show: true,
            position: "inside",
            fontSize: 14,
            fontWeight: "bold",
            formatter: (params) => (params.percent > 0 ? `${params.percent.toFixed(1)}%` : ""),
          },
          labelLine: { show: false },
          data: aleaCodes.map((code) => {
            const color = ALEA_INFO[code].color;
            return {
              name: ALEA_INFO[code].label,
              value: rows.find((r) => r.alea === code)?.val ?? 0,
              itemStyle: { color },
              label: { color: luminance(color) > 0.6 ? "#111" : "#fff" },
            };
          }),
        },
      ],
    };
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start", width: "100%" }}>
      {afficherCourbe && (
        <div style={{ flex: "2 1 320px", minWidth: 280 }}>
          <ResponsiveECharts option={lineOption} style={{ height: 400 }} />
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, flex: "3 1 480px" }}>
        {years.map((year) => (
          <div key={year} style={{ flex: "1 1 200px", minWidth: 180, textAlign: "center" }}>
            <ResponsiveECharts option={pieOptionFor(year)} style={{ height: afficherCourbe ? 220 : 300 }} />
            {/* L'année/réchauffement est affiché dès qu'il y a la courbe
                (page Comprendre) ou plusieurs camemberts côte à côte. */}
            {(afficherCourbe || years.length > 1) && (
              <div style={{ fontWeight: "bold", marginTop: 4 }}>{libelleAnnee(year)}</div>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, width: "100%", justifyContent: "center" }}>
        {aleaCodes.map((code) => (
          <div key={code} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 12,
                height: 12,
                background: ALEA_INFO[code].color,
                display: "inline-block",
                borderRadius: 2,
              }}
            />
            <span style={{ fontSize: 14, color: "#000000" }}>{ALEA_INFO[code].label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}